import React, { useState, useEffect } from 'react';
import { Volume2, X } from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';
import { unlockSoundbox, isSoundboxUnlocked, announce } from '../utils/soundbox';

const DISMISS_KEY = 'soundbox_enable_dismissed_v1';

const SoundPermissionBanner = () => {
  const { showSoundPrompt, setShowSoundPrompt, enableSound, unreadCount } = useNotifications();
  const [collapsed, setCollapsed] = useState(true);
  const [sbDismissed, setSbDismissed] = useState(() => {
    try { return !!localStorage.getItem(DISMISS_KEY); } catch (_) { return false; }
  });
  const [sbUnlocked, setSbUnlocked] = useState(isSoundboxUnlocked);

  // Re-check every few seconds in case page-level gesture unlocked it already
  useEffect(() => {
    if (sbUnlocked) return;
    const t = setInterval(() => { if (isSoundboxUnlocked()) setSbUnlocked(true); }, 2000);
    return () => clearInterval(t);
  }, [sbUnlocked]);

  const handleEnableAll = () => {
    // 1. Enable push/notification sound (existing system)
    if (enableSound) enableSound();
    // 2. Resume AudioContext + prime speechSynthesis for SoundBox TTS
    unlockSoundbox();
    setSbUnlocked(true);
    setSbDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch (_) {}
    if (setShowSoundPrompt) setShowSoundPrompt(false);
    // Play a test chime so user hears confirmation
    setTimeout(() => announce('payment_received', { amount: 0, desc: 'Sound enabled!' }), 300);
  };

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch (_) {}
    setSbDismissed(true);
    if (setShowSoundPrompt) setShowSoundPrompt(false);
  };

  // Show the soundbox-specific banner if not yet unlocked and not dismissed
  const showSbBanner = !sbUnlocked && !sbDismissed;
  // Also show existing notification sound prompt
  if (!showSoundPrompt && !showSbBanner) return null;

  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        data-testid="sound-prompt-collapsed"
        className="fixed bottom-4 right-4 z-40 w-11 h-11 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center animate-pulse"
        aria-label="Enable Sound Alerts"
      >
        <Volume2 className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm animate-in slide-in-from-bottom duration-300">
      <div className="bg-slate-900 border-2 border-emerald-400/60 rounded-2xl p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          <div className="text-3xl">🔊</div>
          <div className="flex-1">
            <div className="font-black text-sm text-emerald-300 mb-0.5">Sound Alerts Enable Karo</div>
            <p className="text-[11px] text-slate-400 mb-3">
              Payment, order, withdrawal — har transaction ki awaaz sunne ke liye enable karo. Browser permission dena zaroori hai.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleEnableAll}
                data-testid="enable-sound-btn"
                className="px-4 py-2 bg-emerald-500 text-slate-900 rounded-lg text-sm font-black hover:bg-emerald-400 transition flex items-center gap-2"
              >
                <Volume2 className="w-4 h-4" />
                Enable Sound Alerts
              </button>
              <button
                onClick={() => setCollapsed(true)}
                className="px-3 py-2 bg-slate-800 rounded-lg text-sm text-slate-400 hover:bg-slate-700 transition"
              >
                Later
              </button>
            </div>
          </div>
          <button onClick={handleDismiss} data-testid="sound-prompt-dismiss" className="p-1 hover:bg-slate-800 rounded">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SoundPermissionBanner;
